'use client';

import {sanitizeUrl} from '@braintree/sanitize-url';
import {
  documentToReactComponents,
  type Options,
} from '@contentful/rich-text-react-renderer';
import {BLOCKS, type Block, type Inline} from '@contentful/rich-text-types';
import {type ReactNode, useCallback, useEffect} from 'react';
import styles from './div.module.scss';
import {type AcceptableDocument, isDocument} from './document-like';

export interface RichDivProps extends GenOptionsProps {
  children?: AcceptableDocument;
  className?: string;
  testId?: string;
}

export interface GenOptionsProps {
  onChatOpenClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onDrawerOpenClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onTelClick?: React.MouseEventHandler<HTMLAnchorElement>;
  setHaveOpenDrawerLink?: (haveLink: boolean) => void;
  classNames?: {
    [k in BLOCKS | 'hyperlink']?: string;
  };
}

type LinkAttributes = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

const OPEN_DRAWER_LINK = 'open-drawer';
const OPEN_CHAT_LINK = 'SUPPORT_CHAT';

const EXTERNAL_LINK_ATTRIBUTES = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const getBlockClasses = (
  classNames: GenOptionsProps['classNames'],
  block: BLOCKS,
) => (classNames?.[block] ? classNames[block] : '');

export interface HyperlinkProps extends GenOptionsProps {
  node: Block | Inline;
  children: ReactNode;
}

const Hyperlink = ({
  node,
  setHaveOpenDrawerLink,
  onDrawerOpenClick,
  onChatOpenClick,
  onTelClick,
  children,
  classNames,
}: HyperlinkProps) => {
  const uri: string | undefined = node.data.uri;
  const additionalAttributes: LinkAttributes = isExternal(uri ?? '')
    ? EXTERNAL_LINK_ATTRIBUTES
    : {};

  const isOpenDrawerLink = uri === OPEN_DRAWER_LINK;
  const isOpenChatLink = uri === OPEN_CHAT_LINK;
  const isTelLink = uri?.startsWith('tel:');

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.stopPropagation();

      if (isOpenDrawerLink) {
        event.preventDefault();
        onDrawerOpenClick?.(event);
      } else if (isOpenChatLink) {
        event.preventDefault();
        onChatOpenClick?.(event);
      } else if (isTelLink) {
        onTelClick?.(event);
      }
    },
    [
      isOpenDrawerLink,
      onDrawerOpenClick,
      isOpenChatLink,
      onChatOpenClick,
      isTelLink,
      onTelClick,
    ],
  );

  useEffect(() => {
    setHaveOpenDrawerLink?.(isOpenDrawerLink);
  }, [isOpenDrawerLink, setHaveOpenDrawerLink]);

  return (
    <a
      className={`${styles.link} ${getBlockClasses(classNames, 'hyperlink' as keyof GenOptionsProps['classNames'])}`}
      // ONLY CHANGE: Using the library to sanitize the URI here
      href={sanitizeUrl(uri ?? '')}
      onClick={onClick}
      {...additionalAttributes}
    >
      {children}
    </a>
  );
};
const genOptions = ({
  onChatOpenClick,
  onDrawerOpenClick,
  onTelClick,
  setHaveOpenDrawerLink,
  classNames,
}: GenOptionsProps): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className={getBlockClasses(classNames, BLOCKS.PARAGRAPH)}>
        {children}
      </p>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul
        className={`${styles.list} ${getBlockClasses(classNames, BLOCKS.UL_LIST)}`}
      >
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol
        className={`${styles.orderedList} ${getBlockClasses(classNames, BLOCKS.OL_LIST)}`}
      >
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li
        className={`${styles.item} ${getBlockClasses(classNames, BLOCKS.LIST_ITEM)}`}
      >
        {children}
      </li>
    ),
    hyperlink: (node, children) => (
      <Hyperlink
        classNames={classNames}
        onChatOpenClick={onChatOpenClick}
        onDrawerOpenClick={onDrawerOpenClick}
        onTelClick={onTelClick}
        setHaveOpenDrawerLink={setHaveOpenDrawerLink}
        node={node}
      >
        {children}
      </Hyperlink>
    ),
  },
  renderText: text => {
    return text
      .split('\n')
      .reduce((acc: ReactNode[], textSegment: string, index: number) => {
        const breakKey = `break-${acc.length}-${textSegment.length}`;
        return [...acc, index > 0 && <br key={breakKey} />, textSegment];
      }, []);
  },
});

/** Will return true if url starts from "http", "https", "://" */
function isExternal(uri: string): boolean {
  return uri.startsWith('http') || uri.startsWith('://');
}

/**
 * Text paragraph that renders rich text. Just pass rich text object and it will render
 * @example <RichDiv>{planDetails as any}</RichDiv>
 * */
export const RichDiv = ({
  children,
  className,
  testId,
  ...props
}: RichDivProps) => {
  if (!isDocument(children)) {
    return null;
  }
  const options = genOptions(props);
  return (
    <div
      className={className}
      data-testid={testId}
    >
      {documentToReactComponents(children, options)}
    </div>
  );
};
